// Chart renderer using Recharts (Bar/Line/Pie) with plain HTML wrappers

import React from "react";
import {
  Bar,
  BarChart as RCBarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart as RCLineChart,
  Pie,
  PieChart as RCPieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { ChartDatum,ChartSpec } from "./mockData";

type Props = { spec: ChartSpec; width?: number; height?: number };

export const ChartRenderer: React.FC<Props> = ({ spec, width = 600, height = 220 }) => {
  if (!spec?.data?.length) return <div style={{ color: "#6B7280" }}>No data</div>;

  const commonStyle: React.CSSProperties = {
    width: "100%",
    maxWidth: width,
    background: "#FFFFFF",
    border: "1px solid #E5E7EB",
    borderRadius: 12,
    padding: 12,
  };

  return (
    <div style={commonStyle}>
      {spec.title && (
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8, color: "#111827" }}>{spec.title}</div>
      )}
      {spec.type === "bar" && <BarChartRc data={spec.data} height={height} />}
      {spec.type === "line" && <LineChartRc data={spec.data} height={height} />}
      {spec.type === "pie" && <PieChartRc data={spec.data} height={height} />}
      {(spec.type !== "pie") && (spec.xLabel || spec.yLabel) && (
        <div style={{ marginTop: 8, display: "flex", justifyContent: "space-between", color: "#6B7280", fontSize: 12 }}>
          <span>{spec.xLabel}</span>
          <span>{spec.yLabel}</span>
        </div>
      )}
    </div>
  );
};

const BarChartRc: React.FC<{ data: ChartDatum[]; height: number }> = ({ data, height }) => (
  <div style={{ width: "100%", height }}>
    <ResponsiveContainer width="100%" height="100%">
      <RCBarChart data={data} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
        <XAxis dataKey="label" tick={{ fontSize: 11 }} axisLine={{ stroke: "#E5E7EB" }} tickLine={{ stroke: "#E5E7EB" }} />
        <YAxis tick={{ fontSize: 11 }} axisLine={{ stroke: "#E5E7EB" }} tickLine={{ stroke: "#E5E7EB" }} />
        <Tooltip />
        <Bar dataKey="value" fill="#111827" radius={[4, 4, 0, 0]} />
      </RCBarChart>
    </ResponsiveContainer>
  </div>
);

const LineChartRc: React.FC<{ data: ChartDatum[]; height: number }> = ({ data, height }) => (
  <div style={{ width: "100%", height }}>
    <ResponsiveContainer width="100%" height="100%">
      <RCLineChart data={data} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
        <XAxis dataKey="label" tick={{ fontSize: 11 }} axisLine={{ stroke: "#E5E7EB" }} tickLine={{ stroke: "#E5E7EB" }} />
        <YAxis tick={{ fontSize: 11 }} axisLine={{ stroke: "#E5E7EB" }} tickLine={{ stroke: "#E5E7EB" }} />
        <Tooltip />
        <Line type="monotone" dataKey="value" stroke="#111827" strokeWidth={2} dot={{ r: 2.5 }} />
      </RCLineChart>
    </ResponsiveContainer>
  </div>
);

const PieChartRc: React.FC<{ data: ChartDatum[]; height: number }> = ({ data, height }) => {
  const colors = ["#111827", "#4B5563", "#9CA3AF", "#D1D5DB", "#6B7280", "#374151"];
  const outerRadius = Math.max(40, Math.min(100, Math.floor(height / 2) - 10));

  return (
    <div style={{ width: "100%", height }}>
      <ResponsiveContainer width="100%" height="100%">
        <RCPieChart>
          <Tooltip />
          <Pie data={data} dataKey="value" nameKey="label" cx="50%" cy="50%" outerRadius={outerRadius}>
            {data.map((_, i) => (
              <Cell key={`cell-${i}`} fill={colors[i % colors.length]} />
            ))}
          </Pie>
        </RCPieChart>
      </ResponsiveContainer>
    </div>
  );
};
